/**
 * Admin Service
 * API service for Super Admin dashboard operations
 */

import apiClient from './api';
import {
  AdminUserListItem,
  AdminUserDetails,
  AdminTeamListItem,
  AdminTeamDetails,
  AdminTeamMember,
  AdminUserListFilters,
  AdminTeamListFilters,
  AdminUserCounts,
  AdminTeamCounts,
  PaginatedResponse,
  ChangePackageRequest,
  ExtendTrialRequest,
  GrantAddonRequest,
  GrantTokenRequest,
  ChangeStatusRequest,
  TeamUsageStats,
  UpdateTeamRequest,
  AdminAgencyListFilters,
  AdminAgencyListItem,
  AdminAgencyDetails,
  CreateAgencyRequest,
  ConvertTeamToAgencyRequest,
  ImpersonationSession,
  StartImpersonationRequest,
  ImpersonationHistoryFilters,
  AdminAffiliateListItem,
  AdminAffiliateDetails,
  AdminAffiliateFilters,
  AffiliateDashboardStats,
  UpdateAffiliateStatusRequest,
  AdminPayoutListItem,
  AdminPayoutFilters,
  ProcessPayoutRequest,
  SystemOverviewStats,
  AIUsageStats,
  PublishingStats,
  FailedJobStats,
  MediaCleanupStats,
  AdminPackageListItem,
  AdminPackageFilters,
  CreatePackageRequest,
  UpdatePackageRequest,
  PackageWcSyncResult,
  UpdatePackageResponse,
  AdminPackageFeatureRow,
  PackageEntitlementsResponse,
  FeatureRegistryResponse,
  PackageQuotaRow,
  SetEntitlementResult,
  PackageFeatureCoverage,
  GatingMode,
  SetPackageFeatureRequest,
  FailedPostItem,
  FailedPostsFilters,
  UpcomingPostItem,
  UpcomingPostsFilters,
  PublishedPostItem,
  PublishedPostsFilters,
  MissedPostItem,
  MissedPostsFilters,
  RevenueStats,
  TrialStats,
  ChannelHealthStats,
  ChurnStats,
  MaintenanceState,
  SystemNotificationListItem,
  CreateNotificationRequest,
  SystemNotificationFilters,
  AuditLogItem,
  AuditLogFilters,
  CreateUserRequest,
  AgencyStaff,
  AgencyStaffRole,
  UpdateUserRequest,
  CreateUserResponse,
  TeamRole,
  AddAgencyStaffAdminRequest,
  ChangeAgencyStaffRoleRequest,
  ChangeAgencyStaffReviewerRequest,
  AttachClientTeamRequest,
  CreateAffiliateRequest,
  UpdateAffiliateRequest,
  UpdateAgencyRequest,
} from '@/types/admin';

// ============================================================================
// USER MANAGEMENT
// ============================================================================

export const getUsers = async (
  filters: AdminUserListFilters = {}
): Promise<PaginatedResponse<AdminUserListItem>> => {
  const params = new URLSearchParams();
  if (filters.search) params.append('search', filters.search);
  if (filters.role) params.append('role', filters.role);
  if (filters.teamId) params.append('teamId', filters.teamId.toString());
  if (filters.isActive !== undefined) params.append('isActive', filters.isActive.toString());
  if (filters.isSuperAdmin !== undefined) params.append('isSuperAdmin', filters.isSuperAdmin.toString());
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.limit) params.append('limit', filters.limit.toString());
  if (filters.sortBy) params.append('sortBy', filters.sortBy);
  if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

  const response = await apiClient.get(`/api/admin/users?${params.toString()}`);
  return { data: response.data.data, meta: response.data.meta };
};

export const getUserById = async (id: number): Promise<AdminUserDetails> => {
  const response = await apiClient.get(`/api/admin/users/${id}`);
  return response.data.data;
};

export const getUserCounts = async (): Promise<AdminUserCounts> => {
  const response = await apiClient.get('/api/admin/users/counts');
  return response.data.data;
};

export const updateUser = async (
  id: number,
  data: UpdateUserRequest
): Promise<void> => {
  await apiClient.put(`/api/admin/users/${id}`, data);
};

export const deleteUser = async (id: number): Promise<void> => {
  await apiClient.delete(`/api/admin/users/${id}`);
};

export const resetUserPassword = async (id: number): Promise<{ resetUrl: string }> => {
  const response = await apiClient.post(`/api/admin/users/${id}/reset-password`);
  return response.data.data;
};

export const changeUserRole = async (
  id: number,
  role: 'USER' | 'ADMIN' | 'AGENCY_ADMIN' | 'SUPER_ADMIN'
): Promise<void> => {
  await apiClient.patch(`/api/admin/users/${id}/role`, { role });
};

export const createUser = async (data: CreateUserRequest): Promise<CreateUserResponse> => {
  const response = await apiClient.post('/api/admin/users', data);
  return response.data.data;
};

export const addAgencyStaff = async (
  userId: number,
  body: { agencyId: number; role: AgencyStaffRole }
): Promise<{ agencyStaff: AgencyStaff }> => {
  const response = await apiClient.post(`/api/admin/users/${userId}/agency-staff`, body);
  return response.data.data;
};

export const removeAgencyStaff = async (
  userId: number,
  agencyId: number
): Promise<void> => {
  await apiClient.delete(`/api/admin/users/${userId}/agency-staff/${agencyId}`);
};

// ============================================================================
// TEAM MANAGEMENT
// ============================================================================

export interface CreateTeamRequest {
  name: string;
  email: string;
  packageId: number;
  expiresAt?: string | null;
  timezone?: string;
  ownerName?: string;
}

export interface CreateTeamResponse {
  team: { id: number; name: string; email: string };
  user: { id: number; email: string; fullName: string };
  resetUrl: string;
}

export const createTeam = async (data: CreateTeamRequest): Promise<CreateTeamResponse> => {
  const response = await apiClient.post('/api/admin/teams', data);
  return response.data.data;
};

export const getTeams = async (
  filters: AdminTeamListFilters = {}
): Promise<PaginatedResponse<AdminTeamListItem>> => {
  const params = new URLSearchParams();
  if (filters.search) params.append('search', filters.search);
  if (filters.packageId) params.append('packageId', filters.packageId.toString());
  if (filters.agencyId) params.append('agencyId', filters.agencyId.toString());
  if (filters.status) params.append('status', filters.status);
  if (filters.isExpired !== undefined) params.append('isExpired', filters.isExpired.toString());
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.limit) params.append('limit', filters.limit.toString());
  if (filters.sortBy) params.append('sortBy', filters.sortBy);
  if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

  const response = await apiClient.get(`/api/admin/teams?${params.toString()}`);
  // Backend returns { success, data: [...], meta: {...} }
  // We need to return { data: [...], meta: {...} }
  return { data: response.data.data, meta: response.data.meta };
};

export const getTeamById = async (id: number): Promise<AdminTeamDetails> => {
  const response = await apiClient.get(`/api/admin/teams/${id}`);
  return response.data.data;
};

export const getTeamCounts = async (): Promise<AdminTeamCounts> => {
  const response = await apiClient.get('/api/admin/teams/counts');
  return response.data.data;
};

export const updateTeam = async (
  id: number,
  data: UpdateTeamRequest
): Promise<void> => {
  await apiClient.put(`/api/admin/teams/${id}`, data);
};

export const deleteTeam = async (id: number): Promise<void> => {
  await apiClient.delete(`/api/admin/teams/${id}`);
};

export const changeTeamPackage = async (id: number, data: ChangePackageRequest): Promise<void> => {
  await apiClient.patch(`/api/admin/teams/${id}/package`, data);
};

export const extendTeamTrial = async (
  id: number,
  data: ExtendTrialRequest
): Promise<{ newExpiresAt: string }> => {
  const response = await apiClient.post(`/api/admin/teams/${id}/extend-trial`, data);
  return response.data.data;
};

export const changeTeamStatus = async (id: number, data: ChangeStatusRequest): Promise<void> => {
  await apiClient.patch(`/api/admin/teams/${id}/status`, data);
};

export const grantAddon = async (
  id: number,
  data: GrantAddonRequest
): Promise<{ addonId: number }> => {
  const response = await apiClient.post(`/api/admin/teams/${id}/grant-addon`, data);
  return response.data.data;
};

export const revokeAddon = async (teamId: number, addonId: number): Promise<void> => {
  await apiClient.post(`/api/admin/teams/${teamId}/revoke-addon/${addonId}`);
};

export const grantTokens = async (
  id: number,
  data: GrantTokenRequest
): Promise<{ addonId: number }> => {
  const response = await apiClient.post(`/api/admin/teams/${id}/grant-tokens`, data);
  return response.data.data;
};

export const getTeamUsage = async (id: number): Promise<TeamUsageStats> => {
  const response = await apiClient.get(`/api/admin/teams/${id}/usage`);
  return response.data.data;
};

export const addTeamMember = async (
  teamId: number,
  body: { userId: number; teamRole: TeamRole }
): Promise<{ user: AdminTeamMember }> => {
  const response = await apiClient.post(`/api/admin/teams/${teamId}/members`, body);
  return response.data.data;
};

export const removeTeamMember = async (
  teamId: number,
  userId: number
): Promise<void> => {
  await apiClient.delete(`/api/admin/teams/${teamId}/members/${userId}`);
};

export const changeMemberRole = async (
  teamId: number,
  userId: number,
  body: { teamRole: TeamRole }
): Promise<{ user: AdminTeamMember }> => {
  const response = await apiClient.patch(
    `/api/admin/teams/${teamId}/members/${userId}/role`,
    body
  );
  return response.data.data;
};

export const transferTeamOwnership = async (
  teamId: number,
  body: { newOwnerUserId: number }
): Promise<{ team: AdminTeamDetails }> => {
  const response = await apiClient.post(
    `/api/admin/teams/${teamId}/transfer-owner`,
    body
  );
  return response.data.data;
};

// ============================================================================
// AGENCY MANAGEMENT
// ============================================================================

export const getAgencies = async (
  filters: AdminAgencyListFilters = {}
): Promise<PaginatedResponse<AdminAgencyListItem>> => {
  const params = new URLSearchParams();
  if (filters.search) params.append('search', filters.search);
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.limit) params.append('limit', filters.limit.toString());
  if (filters.sortBy) params.append('sortBy', filters.sortBy);
  if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

  const response = await apiClient.get(`/api/admin/agencies?${params.toString()}`);
  return { data: response.data.data, meta: response.data.meta };
};

export const getAgencyById = async (id: number): Promise<AdminAgencyDetails> => {
  const response = await apiClient.get(`/api/admin/agencies/${id}`);
  return response.data.data;
};

export interface CreateAgencyResponse {
  agency: { id: number; name: string; email: string };
  user: { id: number; email: string; fullName: string };
  resetUrl: string;
}

export const createAgency = async (data: CreateAgencyRequest): Promise<CreateAgencyResponse> => {
  const response = await apiClient.post('/api/admin/agencies', data);
  return response.data.data;
};

export const updateAgency = async (
  id: number,
  data: Partial<CreateAgencyRequest>
): Promise<void> => {
  await apiClient.put(`/api/admin/agencies/${id}`, data);
};

export const deleteAgency = async (id: number): Promise<void> => {
  await apiClient.delete(`/api/admin/agencies/${id}`);
};

export const convertTeamToAgency = async (
  data: ConvertTeamToAgencyRequest
): Promise<{ agencyId: number; teamId: number }> => {
  const response = await apiClient.post('/api/admin/agencies/convert-team', data);
  return response.data.data;
};

export const getAgencyStaff = async (
  agencyId: number
): Promise<AdminAgencyDetails['staff']> => {
  const agency = await getAgencyById(agencyId);
  return agency.staff;
};

export const getAgencyClients = async (
  agencyId: number
): Promise<AdminAgencyDetails['clients']> => {
  const agency = await getAgencyById(agencyId);
  return agency.clients;
};

// Agency management actions
export const grantAgencyAddon = async (
  agencyId: number,
  data: GrantAddonRequest
): Promise<{ addonId: number }> => {
  const response = await apiClient.post(`/api/admin/agencies/${agencyId}/grant-addon`, data);
  return response.data.data;
};

export const revokeAgencyAddon = async (agencyId: number, addonId: number): Promise<void> => {
  await apiClient.post(`/api/admin/agencies/${agencyId}/revoke-addon/${addonId}`);
};

export const extendAgencyExpiry = async (
  agencyId: number,
  data: { days: number; reason?: string }
): Promise<{ newExpiresAt: string }> => {
  const response = await apiClient.patch(`/api/admin/agencies/${agencyId}/extend-expiry`, data);
  return response.data.data;
};

export const changeAgencyPackage = async (
  agencyId: number,
  data: { packageId: number; reason?: string }
): Promise<void> => {
  await apiClient.patch(`/api/admin/agencies/${agencyId}/package`, data);
};

export const changeAgencyStatus = async (
  agencyId: number,
  data: { status: string; reason?: string }
): Promise<void> => {
  await apiClient.patch(`/api/admin/agencies/${agencyId}/status`, data);
};

// ============================================================================
// AGENCY STAFF ADMIN (Phase 3) — distinct from user-facing addAgencyStaff
// ============================================================================

export const addAgencyStaffAdmin = async (
  agencyId: number,
  body: AddAgencyStaffAdminRequest
): Promise<{ staff: AdminAgencyDetails['staff'][number] }> => {
  const response = await apiClient.post(`/api/admin/agencies/${agencyId}/staff`, body);
  return response.data.data;
};

export const removeAgencyStaffAdmin = async (
  agencyId: number,
  userId: number
): Promise<void> => {
  await apiClient.delete(`/api/admin/agencies/${agencyId}/staff/${userId}`);
};

export const changeAgencyStaffReviewerAdmin = async (
  agencyId: number,
  userId: number,
  body: ChangeAgencyStaffReviewerRequest
): Promise<{ isReviewer: boolean }> => {
  const response = await apiClient.patch(
    `/api/admin/agencies/${agencyId}/staff/${userId}/reviewer`,
    body
  );
  return response.data.data;
};

export const changeAgencyStaffRoleAdmin = async (
  agencyId: number,
  userId: number,
  body: ChangeAgencyStaffRoleRequest
): Promise<{ staff: AdminAgencyDetails['staff'][number] }> => {
  const response = await apiClient.patch(
    `/api/admin/agencies/${agencyId}/staff/${userId}/role`,
    body
  );
  return response.data.data;
};

// ============================================================================
// AGENCY CLIENT TEAMS ADMIN (Phase 3)
// ============================================================================

export const attachClientTeam = async (
  agencyId: number,
  body: AttachClientTeamRequest
): Promise<{ team: AdminTeamListItem }> => {
  const response = await apiClient.post(`/api/admin/agencies/${agencyId}/clients`, body);
  return response.data.data;
};

export const detachClientTeam = async (
  agencyId: number,
  teamId: number
): Promise<{ team: AdminTeamListItem }> => {
  const response = await apiClient.delete(`/api/admin/agencies/${agencyId}/clients/${teamId}`);
  return response.data.data;
};

export const updateAgencyInfo = async (
  agencyId: number,
  data: UpdateAgencyRequest
): Promise<void> => {
  await apiClient.put(`/api/admin/agencies/${agencyId}`, data);
};

// ============================================================================
// AFFILIATE CRUD ADMIN (Phase 3)
// ============================================================================

export const createAffiliate = async (
  body: CreateAffiliateRequest
): Promise<{ affiliate: AdminAffiliateDetails }> => {
  const response = await apiClient.post('/api/admin/affiliates', body);
  return response.data.data;
};

export const updateAffiliate = async (
  id: number,
  body: UpdateAffiliateRequest
): Promise<{ affiliate: AdminAffiliateDetails }> => {
  const response = await apiClient.put(`/api/admin/affiliates/${id}`, body);
  return response.data.data;
};

export const deleteAffiliate = async (id: number): Promise<void> => {
  await apiClient.delete(`/api/admin/affiliates/${id}`);
};

// ============================================================================
// IMPERSONATION (Super Admin Debugging)
// ============================================================================

export const startImpersonation = async (
  data: StartImpersonationRequest
): Promise<{ sessionId: string; token: string; expiresAt: string }> => {
  const response = await apiClient.post('/api/admin/impersonation/start', data);
  return {
    sessionId: response.data.data.session.id,
    token: response.data.data.accessToken,
    expiresAt: response.data.data.session.expiresAt,
  };
};

export const endImpersonation = async (sessionId: string): Promise<{ accessToken: string }> => {
  const response = await apiClient.post('/api/admin/impersonation/end', { sessionId });
  return {
    accessToken: response.data.data.accessToken,
  };
};

export const getActiveImpersonation = async (): Promise<ImpersonationSession | null> => {
  const response = await apiClient.get('/api/admin/impersonation/session');
  return response.data.data;
};

export const getImpersonationHistory = async (
  filters: ImpersonationHistoryFilters = {}
): Promise<PaginatedResponse<ImpersonationSession>> => {
  const params = new URLSearchParams();
  if (filters.adminUserId) params.append('adminUserId', filters.adminUserId.toString());
  if (filters.targetUserId) params.append('targetUserId', filters.targetUserId.toString());
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.limit) params.append('limit', filters.limit.toString());

  const response = await apiClient.get(`/api/admin/impersonation/history?${params.toString()}`);
  return { data: response.data.data, meta: response.data.meta };
};

// ============================================================================
// SYSTEM HEALTH & MONITORING
// ============================================================================

export const getSystemOverview = async (): Promise<SystemOverviewStats> => {
  const response = await apiClient.get('/api/admin/stats/overview');
  return response.data.data;
};

export const getAIUsageStats = async (): Promise<AIUsageStats> => {
  const response = await apiClient.get('/api/admin/stats/ai-usage');
  return response.data.data;
};

export const getPublishingStats = async (): Promise<PublishingStats[]> => {
  const response = await apiClient.get('/api/admin/stats/publishing');
  return response.data.data;
};

export const getFailedPostStats = async (): Promise<FailedJobStats> => {
  const response = await apiClient.get('/api/admin/stats/failed-post-stats');
  return response.data.data;
};

export const getMediaCleanupStats = async (): Promise<MediaCleanupStats> => {
  const response = await apiClient.get('/api/admin/media-cleanup/stats');
  return response.data.data;
};

export const getFailedPosts = async (
  filters: FailedPostsFilters = {}
): Promise<PaginatedResponse<FailedPostItem>> => {
  const params = new URLSearchParams();
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.limit) params.append('limit', filters.limit.toString());
  if (filters.platform) params.append('platform', filters.platform);
  if (filters.hours !== undefined) params.append('hours', filters.hours.toString());

  const response = await apiClient.get(`/api/admin/stats/failed-posts?${params.toString()}`);
  return response.data;
};

export const getUpcomingPosts = async (
  filters: UpcomingPostsFilters = {}
): Promise<PaginatedResponse<UpcomingPostItem>> => {
  const params = new URLSearchParams();
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.limit) params.append('limit', filters.limit.toString());
  if (filters.hours) params.append('hours', filters.hours.toString());
  if (filters.platform) params.append('platform', filters.platform);

  const response = await apiClient.get(`/api/admin/stats/upcoming-posts?${params.toString()}`);
  return response.data;
};

export const getPublishedPosts = async (
  filters: PublishedPostsFilters = {}
): Promise<PaginatedResponse<PublishedPostItem>> => {
  const params = new URLSearchParams();
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.limit) params.append('limit', filters.limit.toString());
  if (filters.hours !== undefined) params.append('hours', filters.hours.toString());
  if (filters.platform) params.append('platform', filters.platform);

  const response = await apiClient.get(`/api/admin/stats/published-posts?${params.toString()}`);
  return response.data;
};

export const getMissedPosts = async (
  filters: MissedPostsFilters = {}
): Promise<PaginatedResponse<MissedPostItem>> => {
  const params = new URLSearchParams();
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.limit) params.append('limit', filters.limit.toString());
  if (filters.platform) params.append('platform', filters.platform);
  if (filters.hours !== undefined) params.append('hours', filters.hours.toString());

  const response = await apiClient.get(`/api/admin/stats/missed-posts?${params.toString()}`);
  return response.data;
};

export const retryFailedPost = async (id: number): Promise<void> => {
  await apiClient.post(`/api/admin/stats/retry-failed-post/${id}`);
};

// ============================================================================
// AFFILIATE MANAGEMENT
// ============================================================================

export const getAffiliateDashboard = async (): Promise<AffiliateDashboardStats> => {
  const response = await apiClient.get('/api/admin/affiliates/dashboard');
  return response.data.data;
};

export const getAffiliates = async (
  filters: AdminAffiliateFilters = {}
): Promise<PaginatedResponse<AdminAffiliateListItem>> => {
  const params = new URLSearchParams();
  if (filters.search) params.append('search', filters.search);
  if (filters.status) params.append('status', filters.status);
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.limit) params.append('limit', filters.limit.toString());
  if (filters.sortBy) params.append('sortBy', filters.sortBy);
  if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

  const response = await apiClient.get(`/api/admin/affiliates?${params.toString()}`);
  return { data: response.data.data, meta: response.data.meta };
};

export const getAffiliateById = async (id: number): Promise<AdminAffiliateDetails> => {
  const response = await apiClient.get(`/api/admin/affiliates/${id}`);
  return response.data.data;
};

export const updateAffiliateStatus = async (
  id: number,
  data: UpdateAffiliateStatusRequest
): Promise<void> => {
  await apiClient.patch(`/api/admin/affiliates/${id}/status`, data);
};

export const getPayouts = async (
  filters: AdminPayoutFilters = {}
): Promise<PaginatedResponse<AdminPayoutListItem>> => {
  const params = new URLSearchParams();
  if (filters.status) params.append('status', filters.status);
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.limit) params.append('limit', filters.limit.toString());
  if (filters.sortBy) params.append('sortBy', filters.sortBy);
  if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

  const response = await apiClient.get(`/api/admin/affiliates/payouts?${params.toString()}`);
  return { data: response.data.data, meta: response.data.meta };
};

export const processPayout = async (
  id: number,
  data: ProcessPayoutRequest
): Promise<void> => {
  await apiClient.patch(`/api/admin/affiliates/payouts/${id}`, data);
};

export const updateAffiliateCommissionRate = async (
  affiliateId: number,
  rate: number | null
): Promise<void> => {
  await apiClient.patch(`/api/admin/affiliates/${affiliateId}/commission-rate`, { rate });
};

// ============================================================================
// PACKAGE MANAGEMENT
// ============================================================================

export const getPackages = async (
  filters: AdminPackageFilters = {}
): Promise<PaginatedResponse<AdminPackageListItem>> => {
  const params = new URLSearchParams();
  if (filters.search) params.append('search', filters.search);
  if (filters.isActive !== undefined) params.append('isActive', filters.isActive.toString());
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.limit) params.append('limit', filters.limit.toString());
  if (filters.sortBy) params.append('sortBy', filters.sortBy);
  if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

  const response = await apiClient.get(`/api/admin/packages?${params.toString()}`);
  return { data: response.data.data, meta: response.data.meta };
};

export const getPackageById = async (id: number): Promise<AdminPackageListItem> => {
  const response = await apiClient.get(`/api/admin/packages/${id}`);
  return response.data.data;
};

export const createPackage = async (
  data: CreatePackageRequest
): Promise<PackageWcSyncResult> => {
  const response = await apiClient.post('/api/admin/packages', data);
  return response.data.data;
};

export const updatePackage = async (
  id: number,
  data: UpdatePackageRequest
): Promise<UpdatePackageResponse> => {
  const response = await apiClient.put(`/api/admin/packages/${id}`, data);
  return { package: response.data.data, wcSync: response.data.wcSync };
};

export const deletePackage = async (id: number): Promise<void> => {
  await apiClient.delete(`/api/admin/packages/${id}`);
};

export const clonePackage = async (id: number): Promise<{ id: number; name: string }> => {
  const response = await apiClient.post(`/api/admin/packages/${id}/clone`);
  return response.data.data;
};

/**
 * Retry WC product provisioning for a package whose shop sync previously
 * failed (or never ran, e.g. it was Free at creation and later given a
 * price). Idempotent. Returns the updated package.
 */
export const publishPackageToShop = async (id: number): Promise<PackageWcSyncResult> => {
  const response = await apiClient.post(`/api/admin/packages/${id}/publish-to-shop`);
  return response.data.data;
};

/**
 * Create/update an elastic package feature row (e.g. `wordpress`). This is
 * the generic feature registry used by boolean/integer/string features that
 * don't have a dedicated Package column — see AdminPackageFeatureRow.
 */
export const setPackageFeature = async (
  packageId: number,
  data: SetPackageFeatureRequest
): Promise<AdminPackageFeatureRow> => {
  const response = await apiClient.post(`/api/admin/packages/${packageId}/features`, data);
  return response.data.data.feature;
};

export const getFeatureRegistry = async (): Promise<FeatureRegistryResponse> => {
  const response = await apiClient.get('/api/admin/packages/feature-registry');
  return response.data.data;
};

export const getQuotaRegistry = async (): Promise<Omit<PackageQuotaRow, 'monthlyLimit'>[]> => {
  const response = await apiClient.get('/api/admin/packages/quota-registry');
  return response.data.data;
};

export const getPackageEntitlements = async (
  packageId: number
): Promise<PackageEntitlementsResponse> => {
  const response = await apiClient.get(`/api/admin/packages/${packageId}/entitlements`);
  return response.data.data;
};

export const setPackageEntitlement = async (
  packageId: number,
  key: string,
  enabled: boolean
): Promise<SetEntitlementResult> => {
  const response = await apiClient.put(
    `/api/admin/packages/${packageId}/entitlements/${encodeURIComponent(key)}`,
    { enabled }
  );
  return response.data.data;
};

export const getPackageQuotas = async (packageId: number): Promise<PackageQuotaRow[]> => {
  const response = await apiClient.get(`/api/admin/packages/${packageId}/quotas`);
  return response.data.data;
};

export const setPackageQuota = async (
  packageId: number,
  quotaKey: string,
  monthlyLimit: number | null
): Promise<void> => {
  await apiClient.put(`/api/admin/packages/${packageId}/quotas/${encodeURIComponent(quotaKey)}`, {
    monthlyLimit,
  });
};

export const getPackageFeatureCoverage = async (): Promise<PackageFeatureCoverage> => {
  const response = await apiClient.get('/api/admin/packages/feature-coverage');
  return response.data.data;
};

/** Global gating mode, read from the caller's own team package payload. Null when unavailable. */
export const getGatingMode = async (): Promise<GatingMode | null> => {
  const response = await apiClient.get('/api/teams/package');
  const mode: unknown = response.data?.data?.entitlements?.mode;
  return mode === 'off' || mode === 'shadow' || mode === 'enforce' ? mode : null;
};

// ============================================================================
// DASHBOARD ANALYTICS
// ============================================================================

export const getRevenueStats = async (): Promise<RevenueStats> => {
  const response = await apiClient.get('/api/admin/stats/revenue');
  return response.data.data;
};

export const getTrialStats = async (): Promise<TrialStats> => {
  const response = await apiClient.get('/api/admin/stats/trials');
  return response.data.data;
};

export const getChannelHealthStats = async (): Promise<ChannelHealthStats> => {
  const response = await apiClient.get('/api/admin/stats/channel-health');
  return response.data.data;
};

export const getChurnStats = async (): Promise<ChurnStats> => {
  const response = await apiClient.get('/api/admin/stats/churn');
  return response.data.data;
};

export const getMaintenanceStatus = async (): Promise<MaintenanceState> => {
  const response = await apiClient.get('/api/admin/settings/maintenance');
  return response.data.data;
};

export const setMaintenanceMode = async (data: { enabled: boolean; message?: string }): Promise<MaintenanceState> => {
  const response = await apiClient.post('/api/admin/settings/maintenance', data);
  return response.data.data;
};

// ============================================================================
// SYSTEM NOTIFICATIONS
// ============================================================================

export const getSystemNotifications = async (
  filters: SystemNotificationFilters = {}
): Promise<PaginatedResponse<SystemNotificationListItem>> => {
  const params = new URLSearchParams();
  if (filters.type) params.append('type', filters.type);
  if (filters.severity) params.append('severity', filters.severity);
  if (filters.targetType) params.append('targetType', filters.targetType);
  if (filters.isActive !== undefined) params.append('isActive', filters.isActive.toString());
  if (filters.createdFrom) params.append('createdFrom', filters.createdFrom);
  if (filters.createdTo) params.append('createdTo', filters.createdTo);
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.limit) params.append('limit', filters.limit.toString());

  const response = await apiClient.get(`/api/admin/notifications?${params.toString()}`);
  // Backend returns { success, data, meta } - return { data, meta } for PaginatedResponse
  return {
    data: response.data.data,
    meta: response.data.meta,
  };
};

export const getSystemNotification = async (id: number): Promise<SystemNotificationListItem> => {
  const response = await apiClient.get(`/api/admin/notifications/${id}`);
  return response.data.data;
};

export const createSystemNotification = async (
  data: CreateNotificationRequest
): Promise<SystemNotificationListItem> => {
  const response = await apiClient.post('/api/admin/notifications', data);
  return response.data.data;
};

export const updateSystemNotification = async (
  id: number,
  data: Partial<CreateNotificationRequest>
): Promise<SystemNotificationListItem> => {
  const response = await apiClient.put(`/api/admin/notifications/${id}`, data);
  return response.data.data;
};

export const deleteSystemNotification = async (id: number): Promise<void> => {
  await apiClient.delete(`/api/admin/notifications/${id}`);
};

export const publishSystemNotification = async (id: number): Promise<void> => {
  await apiClient.post(`/api/admin/notifications/${id}/publish`);
};

export const unpublishSystemNotification = async (id: number): Promise<void> => {
  await apiClient.post(`/api/admin/notifications/${id}/unpublish`);
};

// ============================================================================
// AUDIT LOGS
// ============================================================================

export const getAuditLogs = async (
  filters: AuditLogFilters = {}
): Promise<PaginatedResponse<AuditLogItem>> => {
  const params = new URLSearchParams();
  if (filters.adminUserId) params.append('adminUserId', filters.adminUserId.toString());
  if (filters.action) params.append('action', filters.action);
  if (filters.entityType) params.append('entityType', filters.entityType);
  if (filters.entityId) params.append('entityId', filters.entityId.toString());
  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.limit) params.append('limit', filters.limit.toString());

  const response = await apiClient.get(`/api/admin/audit-logs?${params.toString()}`);
  return { data: response.data.data, meta: response.data.meta };
};

export const getEntityAuditLogs = async (
  entityType: string,
  entityId: number,
  page: number = 1,
  limit: number = 20
): Promise<PaginatedResponse<AuditLogItem>> => {
  const response = await apiClient.get(
    `/api/admin/audit-logs/entity/${entityType}/${entityId}?page=${page}&limit=${limit}`
  );
  return { data: response.data.data, meta: response.data.meta };
};

// ============================================================================
// EXPORT
// ============================================================================

export const adminService = {
  // Users
  getUsers,
  getUserById,
  getUserCounts,
  createUser,
  updateUser,
  deleteUser,
  resetUserPassword,
  changeUserRole,
  addAgencyStaff,
  removeAgencyStaff,
  // Teams
  createTeam,
  getTeams,
  getTeamById,
  getTeamCounts,
  updateTeam,
  deleteTeam,
  changeTeamPackage,
  extendTeamTrial,
  changeTeamStatus,
  grantAddon,
  revokeAddon,
  grantTokens,
  getTeamUsage,
  addTeamMember,
  removeTeamMember,
  changeMemberRole,
  transferTeamOwnership,
  // Agencies
  getAgencies,
  getAgencyById,
  createAgency,
  updateAgency,
  updateAgencyInfo,
  deleteAgency,
  convertTeamToAgency,
  getAgencyStaff,
  getAgencyClients,
  grantAgencyAddon,
  revokeAgencyAddon,
  extendAgencyExpiry,
  changeAgencyPackage,
  changeAgencyStatus,
  // Agency staff admin (Phase 3)
  addAgencyStaffAdmin,
  removeAgencyStaffAdmin,
  changeAgencyStaffRoleAdmin,
  changeAgencyStaffReviewerAdmin,
  // Agency clients admin (Phase 3)
  attachClientTeam,
  detachClientTeam,
  // Impersonation
  startImpersonation,
  endImpersonation,
  getActiveImpersonation,
  getImpersonationHistory,
  // System Health
  getSystemOverview,
  getAIUsageStats,
  getPublishingStats,
  getFailedPostStats,
  getMediaCleanupStats,
  getFailedPosts,
  getUpcomingPosts,
  getPublishedPosts,
  getMissedPosts,
  retryFailedPost,
  // Affiliates
  getAffiliateDashboard,
  getAffiliates,
  getAffiliateById,
  updateAffiliateStatus,
  updateAffiliateCommissionRate,
  createAffiliate,
  updateAffiliate,
  deleteAffiliate,
  getPayouts,
  processPayout,
  // Packages
  getPackages,
  getPackageById,
  createPackage,
  updatePackage,
  deletePackage,
  clonePackage,
  publishPackageToShop,
  setPackageFeature,
  getFeatureRegistry,
  getQuotaRegistry,
  getPackageEntitlements,
  setPackageEntitlement,
  getPackageQuotas,
  setPackageQuota,
  getPackageFeatureCoverage,
  getGatingMode,
  // Dashboard Analytics
  getRevenueStats,
  getTrialStats,
  getChannelHealthStats,
  getChurnStats,
  getMaintenanceStatus,
  setMaintenanceMode,
  // System Notifications
  getSystemNotifications,
  getSystemNotification,
  createSystemNotification,
  updateSystemNotification,
  deleteSystemNotification,
  publishSystemNotification,
  unpublishSystemNotification,
  // Audit Logs
  getAuditLogs,
  getEntityAuditLogs,
};
